/**
 * EmployeeWeekLoadRow.tsx
 * One person's row in the Employee Schedule L1 redesign (2026-07-25):
 *
 *  collapsed — a load pill per week ("80%" / dashed "free" / blue "away" /
 *              red "N% ▲" on a day-off conflict) with tiny ticks when the week
 *              holds weekend work (teal), a worked stat (violet) or an
 *              over-100% day (orange), so the signals survive collapse. The %
 *              is the intensity-aware utilization (average daily load — a 10%
 *              reviewer present all week reads 10%, BUG-2026-0196), not the
 *              presence-only busy-days figure that hid light shares behind 100%.
 *  open      — each week cell morphs into the 7-day LOAD BARS (Sat+Sun first,
 *              3px breath before the weekdays): bar height = Σ day-share %,
 *              dashed 100% reference line, 150% cap; green ≤100, orange >100,
 *              teal weekend load, violet stat load, blue absence, 3px ground
 *              line when free. The week's busy-% prints on a RESERVED line
 *              beneath so all strips share one baseline. Hover any day for the
 *              arithmetic ("Thu — 100% · Point Load 60% + Cerchar 40%").
 *
 * All figures come from utils/personDayLoad (derived from the assignment bars)
 * — this component only draws.
 */

import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { EmployeeAvatar } from '../../pages/schedule/views/skill-matrix/EmployeeAvatar';
import { dayLoadTitle, type DayLoad, type WeekLoadAggregate } from '../../utils/personDayLoad';
import type { EmployeeBarUser } from '../../services/scheduleEmployeeBars';

export const LOAD_NAME_W = 300;
export const LOAD_WEEK_W = 140;
const ROW_H = 44;
const ROW_H_OPEN = 52;
/** Bar height mapping: 100% = 17px on a 26px scale capped at 150%. */
const BAR_MAX_H = 26;
const BAR_CAP_PCT = 150;
const REF_LINE_H = Math.round((100 / BAR_CAP_PCT) * BAR_MAX_H); // 17

function barHeight(load: number): number {
  if (load <= 0) return 3;
  return Math.max(4, Math.round((Math.min(load, BAR_CAP_PCT) / BAR_CAP_PCT) * BAR_MAX_H));
}

const Tick: React.FC<{ color: string; round?: boolean; title: string }> = ({ color, round, title }) => (
  <Box component="span" title={title} sx={{ width: 6, height: 6, borderRadius: round ? '50%' : '2px', backgroundColor: color, flexShrink: 0 }} />
);

/** Round-12 polish: two shipped pill styles, user-switchable (default 'tile'). */
export type WeekPillStyle = 'tile' | 'barfill';

/** Original matrix heat bands: ≤70% green tint · 71–100% amber tint. */
function heatTint(theme: Theme, pct: number): string {
  return pct <= 70
    ? alpha(theme.schedule.heatmapLow, 0.2)
    : alpha(theme.schedule.heatmapMid, 0.28);
}

const WeekPill: React.FC<{ agg: WeekLoadAggregate; hasConflict: boolean; pillStyle: WeekPillStyle }> = ({ agg, hasConflict, pillStyle }) => {
  const theme = useTheme();
  const s = theme.schedule;
  const tile = pillStyle === 'tile';
  const base = {
    width: tile ? '92%' : '90%', height: tile ? 34 : 20, borderRadius: tile ? '6px' : '10px', px: 0.75,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
    fontSize: tile ? '0.72rem' : '0.64rem', fontWeight: 800,
  } as const;
  // Free weeks: a quiet dash — no pill, no cage (round-12 "the repetitive free
  // is annoying" fix).
  if (!agg.any) {
    return <Typography component="span" sx={{ fontSize: '0.72rem', color: alpha(theme.palette.text.secondary, 0.45) }}>—</Typography>;
  }
  if (agg.away) {
    return <Box sx={{ ...base, color: theme.palette.common.white, backgroundColor: s.absenceVacation }}>away</Box>;
  }
  const ticks = (
    <>
      {agg.weekendWork && <Tick color={s.weekendWork} title="weekend work this week" />}
      {agg.statWorked && <Tick color={s.statWorked} round title="worked a stat holiday this week" />}
      {agg.over && <Tick color={s.heatmapElevated} title="a day runs over 100% this week" />}
    </>
  );
  // BUG-2026-0196: the pill prints the intensity-aware utilization (average
  // daily load) — presence-% read "100%" for a 10% reviewer on every task.
  const pct = agg.utilizationPercent;
  if (hasConflict) {
    return (
      <Box title="assigned on a day off" sx={{ ...base, color: theme.palette.common.white, backgroundColor: s.conflictHard }}>
        {ticks}{pct}% ▲
      </Box>
    );
  }
  if (pillStyle === 'barfill') {
    // Track + proportional heat fill: the pill's fill length IS the load %.
    const fillColor = pct <= 70 ? s.heatmapLow : s.heatmapMid;
    return (
      <Box sx={{ ...base, position: 'relative', overflow: 'hidden', color: theme.palette.text.primary, backgroundColor: alpha(s.noActivity, 0.1), border: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
        <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(pct, 100)}%`, backgroundColor: alpha(fillColor, 0.45) }} />
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '4px' }}>
          {ticks}{pct}%
        </Box>
      </Box>
    );
  }
  // 'tile' — the pill fills the cell, heat-tinted (the original matrix feel).
  return (
    <Box sx={{ ...base, color: theme.palette.text.primary, backgroundColor: heatTint(theme, pct) }}>
      {ticks}{pct}%
    </Box>
  );
};

const DayBar: React.FC<{ day: DayLoad; accentFor?: (day: DayLoad) => string | null }> = ({ day, accentFor }) => {
  const theme = useTheme();
  const s = theme.schedule;
  let color: string | null = null;
  let h = barHeight(day.load);
  if (day.away && day.load === 0) { color = s.absenceVacation; h = 14; }
  else if (day.load > 0) {
    color = day.stat ? s.statWorked
      : day.weekend ? s.weekendWork
      : day.load > 100 ? s.heatmapElevated
      // Host-supplied accent (Scenario Phase 3: slate on days carrying this
      // draft's share) — only replaces the healthy green, never a signal colour.
      : accentFor?.(day) ?? s.heatmapLow;
  }
  // One day slot = exactly 1/7 of the week cell, so these bars sit on the SAME
  // grid as the accordion's task bars beneath (v1.1.44 — the centered 10px
  // cluster drifted off the per-day breakdown).
  return (
    <Box component="span" title={dayLoadTitle(day)} sx={{ flex: 1, minWidth: 0, px: '1px', display: 'flex', alignItems: 'flex-end' }}>
      <Box
        component="span"
        sx={{
          width: '100%', minHeight: 3, height: h, borderRadius: '2px 2px 0 0',
          backgroundColor: color ?? alpha(s.noActivity, 0.35),
          ...(color === null && { height: 3, borderRadius: '1px' }),
          ...(day.load > 100 && { boxShadow: `0 0 5px ${alpha(s.heatmapElevated, 0.55)}` }),
        }}
      />
    </Box>
  );
};

/** Exported for the Scenario Phase-3 grid (v1.1.47) — the open row's week cell
 *  morphs into these 7-day load bars there too, over the same day grid. */
export const WeekLoadBars: React.FC<{
  agg: WeekLoadAggregate;
  accentFor?: (day: DayLoad) => string | null;
  /** Appended to the busy-% line (Phase 3 passes ' of week booked' — under the
   *  DAY bars a bare "80%" read as a day figure). */
  pctSuffix?: string;
  /** Overrides the %-line figure (defaults to the aggregate's intensity-aware
   *  utilizationPercent — presence-% hid a 25% mentor behind "80%"). */
  pctValue?: number;
}> = ({ agg, accentFor, pctSuffix, pctValue }) => {
  const theme = useTheme();
  const pctColor = agg.away ? theme.schedule.absenceVacation
    : agg.over ? theme.schedule.heatmapElevated
    : theme.palette.text.secondary;
  return (
    <>
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-end', height: BAR_MAX_H + 1, width: '100%' }}>
        {/* dashed 100% reference line behind the bars */}
        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: REF_LINE_H, borderTop: `1px dashed ${alpha(theme.schedule.committed, 0.35)}`, pointerEvents: 'none' }} />
        {agg.days.map((d) => <DayBar key={d.date} day={d} accentFor={accentFor} />)}
      </Box>
      {/* reserved %-line — always present so every week's bars share a baseline */}
      <Typography component="span" sx={{ fontSize: '0.6rem', fontWeight: 800, height: 14, lineHeight: '14px', color: pctColor }}>
        {!agg.any ? ' ' : agg.away ? 'away' : `${pctValue ?? agg.utilizationPercent}%${pctSuffix ?? ''}`}
      </Typography>
    </>
  );
};

export interface EmployeeWeekLoadRowProps {
  user: EmployeeBarUser;
  aggregates: WeekLoadAggregate[];
  /** Weeks with a task-on-day-off conflict (red pill), by weekStart. */
  conflictWeeks: ReadonlySet<string>;
  /** Collapsed pill rendering — 'tile' (full cell, heat tint) or 'barfill'. */
  pillStyle: WeekPillStyle;
  open: boolean;
  onToggle: () => void;
}

export const EmployeeWeekLoadRow: React.FC<EmployeeWeekLoadRowProps> = ({
  user, aggregates, conflictWeeks, pillStyle, open, onToggle,
}) => {
  const theme = useTheme();
  return (
    <Box
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      sx={{
        display: 'flex', alignItems: 'stretch', height: open ? ROW_H_OPEN : ROW_H, cursor: 'pointer',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        // 2026-07-26 (third round of "less highlight"): the OPEN row carries NO
        // tint at all — the morphed day bars are the open signal. Hover is a
        // neutral near-nothing wash (no sage cast), and the focus ring is a
        // faint inset line instead of a solid 2px outline.
        backgroundColor: theme.palette.background.paper,
        transition: 'background-color 150ms, height 280ms ease',
        '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.015) },
        '&:hover .load-cta': { opacity: 1 },
        '&:focus-visible': { outline: 'none', boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}` },
      }}
    >
      <Box sx={{ width: LOAD_NAME_W, flexShrink: 0, position: 'sticky', left: 0, zIndex: 2, backgroundColor: 'inherit', display: 'flex', alignItems: 'center', gap: 1, px: 1, minWidth: 0, borderRight: `1px solid ${theme.palette.divider}` }}>
        <ChevronRightIcon sx={{ fontSize: 14, color: 'text.secondary', transition: 'transform 220ms', transform: open ? 'rotate(90deg)' : 'none', flexShrink: 0 }} />
        <EmployeeAvatar displayName={user.displayName} email={user.email} photoUrl={user.photoUrl} size={28} />
        <Typography className="load-cta" sx={{ ml: 'auto', fontSize: '0.56rem', fontWeight: 800, color: theme.palette.primary.main, opacity: 0, transition: 'opacity 120ms', flexShrink: 0 }}>
          {open ? 'CLOSE ✕' : 'FOCUS ↕'}
        </Typography>
      </Box>
      {aggregates.map((agg) => (
        <Box key={agg.weekStart} sx={{ width: LOAD_WEEK_W, flexShrink: 0, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.5)}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
          {open
            ? <WeekLoadBars agg={agg} />
            : <WeekPill agg={agg} hasConflict={conflictWeeks.has(agg.weekStart)} pillStyle={pillStyle} />}
        </Box>
      ))}
    </Box>
  );
};
